import { useState, useEffect } from 'react';
import Countdown from './Countdown';
import { usePromoCode, getPromoWithNearestExpiry } from '@/hooks/use-promo-code';
import { shouldShowCountdown } from '@/lib/countdown';

interface FloatingCountdownProps {
  onVisibilityChange?: (isVisible: boolean) => void;
  className?: string;
}

const FloatingCountdown = ({ onVisibilityChange, className }: FloatingCountdownProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const { eventCode, eventData, referralCode, referralData } = usePromoCode();

  const countdownData = getPromoWithNearestExpiry(eventData, referralData);
  const countdownTargetDate = countdownData?.expires_at
    ? new Date(countdownData.expires_at * 1000)
    : null;

  useEffect(() => {
    const handleScroll = () => {
      // Show floating countdown when scrolled down more than 100vh (past the hero section)
      const scrollY = window.scrollY;
      const shouldShow = scrollY > window.innerHeight;
      setIsVisible(shouldShow);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Notify parent when visibility changes
  useEffect(() => {
    if (countdownTargetDate && countdownData?.valid && shouldShowCountdown(countdownTargetDate)) {
      onVisibilityChange?.(isVisible);
    } else {
      onVisibilityChange?.(false);
    }
  }, [isVisible, countdownTargetDate, countdownData?.valid, onVisibilityChange]);

  if (!countdownTargetDate || !countdownData?.valid || !shouldShowCountdown(countdownTargetDate)) {
    return null;
  }

  return (
    <div
      className={`${className} fixed lg:bottom-4 lg:left-1/2 lg:transform lg:-translate-x-1/2 lg:top-auto w-full lg:w-auto lg:z-50 transition-all duration-300 h-9 lg:h-auto ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 lg:translate-y-4 pointer-events-none'
        }`}
    >
      <div className="bg-slate-900 rounded-tr-xl rounded-tl-xl lg:rounded-xl border-t border-b border-slate-900 lg:border lg:border-slate-800 px-4 lg:px-6 lg:py-3 h-full flex items-center lg:shadow-lg">
        <Countdown
          targetDate={countdownTargetDate}
          className="mx-auto"
          textColor="text-slate-50"
          numberColor="text-red-400"
          promoCode={eventCode || referralCode}
        />
      </div>
    </div>
  );
};

export default FloatingCountdown;
